import dayjs from 'dayjs';

export const APP_DATE_FORMAT = 'DD.MM.YYYY';
export const APP_DATE_TIME_FORMAT = 'DD.MM.YYYY HH:mm';
export const ISO_DATE_FORMAT = 'YYYY-MM-DD';

// Date formatting utility
export const formatDate = (date: string | Date | dayjs.Dayjs | null | undefined): string => {
  if (!date) return '';
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format(APP_DATE_FORMAT) : typeof date === 'string' ? date : '';
};

export const formatDateTime = (date: string | Date | dayjs.Dayjs | null | undefined): string => {
  if (!date) return '';
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format(APP_DATE_TIME_FORMAT) : typeof date === 'string' ? date : '';
};

// Convert date to yyyy-MM-dd for HTML date input (type="date")
export const toDateInputValue = (date: string | Date | dayjs.Dayjs | null | undefined): string => {
  if (!date) return '';
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format(ISO_DATE_FORMAT) : '';
};

// Builds a wa.me deep link from a customer/employee phone number, or null if there's
// nothing usable to link. wa.me needs digits only, no leading 0 or '+', full country code.
// Numbers already in international format (+355..., 00355...) are normalized as-is;
// a local Albanian-style number (leading 0, no country code) is assumed to be a domestic
// Albania (+355) number, since that's this product's home market - adjust here if RentMan
// starts operating with customers from other default countries.
export const getWhatsAppLink = (phoneNumber: string | null | undefined): string | null => {
  if (!phoneNumber) return null;
  const digitsOnly = phoneNumber.replace(/[^\d+]/g, '');
  if (!digitsOnly) return null;

  let normalized: string;
  if (digitsOnly.startsWith('+')) {
    normalized = digitsOnly.slice(1);
  } else if (digitsOnly.startsWith('00')) {
    normalized = digitsOnly.slice(2);
  } else if (digitsOnly.startsWith('0')) {
    normalized = `355${digitsOnly.slice(1)}`;
  } else {
    normalized = digitsOnly;
  }

  return normalized ? `https://wa.me/${normalized}` : null;
};

// Currency formatting utility.
// The active currency is the rental company's single billing currency (see
// Company.Currency on the backend) - there is no per-record currency or
// conversion. `setActiveCurrency` is called once by CurrencyProvider whenever
// the logged-in user's company currency is known (login/app load), so every
// call site below picks up the right symbol/decimals without needing the
// currency threaded through as a parameter.
export type Currency = 'LEK' | 'EUR';

interface CurrencyConfig {
  symbol: string;
  decimalPlaces: number;
  symbolBefore: boolean;
}

const CURRENCY_CONFIG: Record<Currency, CurrencyConfig> = {
  EUR: { symbol: '\u20AC', decimalPlaces: 2, symbolBefore: true },
  LEK: { symbol: 'Lek', decimalPlaces: 0, symbolBefore: false },
};

let activeCurrency: Currency = 'EUR';

export const setActiveCurrency = (currency: string | null | undefined): void => {
  activeCurrency = currency === 'LEK' ? 'LEK' : 'EUR';
  CURRENCY_SYMBOL = CURRENCY_CONFIG[activeCurrency].symbol;
};

export const getActiveCurrency = (): Currency => activeCurrency;

export const getCurrencyConfig = (currency: Currency = activeCurrency): CurrencyConfig => CURRENCY_CONFIG[currency];

// Kept as a live-updating export (not a const) so the ~7 existing call sites
// that render it as a bare input adornment stay correct after setActiveCurrency runs.
export let CURRENCY_SYMBOL = CURRENCY_CONFIG.EUR.symbol;

export const formatCurrency = (amount: number | string | null | undefined, decimalPlaces?: number): string => {
  const config = CURRENCY_CONFIG[activeCurrency];
  const numAmount = amount === null || amount === undefined
    ? 0
    : typeof amount === 'string' ? parseFloat(amount) : amount;
  const safeAmount = isNaN(numAmount) ? 0 : numAmount;
  const resolvedDecimalPlaces = decimalPlaces ?? config.decimalPlaces;
  const number = safeAmount.toLocaleString('en-US', {
    minimumFractionDigits: resolvedDecimalPlaces,
    maximumFractionDigits: resolvedDecimalPlaces,
  });
  return config.symbolBefore ? `${config.symbol}${number}` : `${number} ${config.symbol}`;
};

